import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, Stack, Typography } from '@mui/material';

import LogoSection from '../LogoSection';
import EntitySelect from 'Commons/select/entitySelectNew';
import ProfileSection from './ProfileSection';
import NotificationSection from './NotificationSection';
import { useDispatch, useSelector } from 'store';
import RoleGuard from 'components/RoleGuard';
import { openDrawer } from 'store/slices/menu';
import { changeDefaultEntityId } from 'store/slices/user';
import useAuth from 'hooks/useAuth';
import { IconMenu2 } from '@tabler/icons';
import { openSnackbar } from 'store/slices/snackbar';
import useNetworkStatus from 'utils/useNetworkStatus';

const Header = () => {
  const isOnline = useNetworkStatus();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { drawerOpen } = useSelector((state) => state.menu);
  const { user } = useAuth();
  const [network, setNetwork] = useState(true);

  // Network status effect
  useEffect(() => {
    if (!isOnline) {
      setNetwork(false);
      dispatch(
        openSnackbar({
          open: true,
          message: 'No internet connection',
          variant: 'alert',
          alert: { color: 'error' },
        })
      );
    } else if (!network) {
      setNetwork(true);
      dispatch(
        openSnackbar({
          open: true,
          message: 'Connection established successfully',
          variant: 'alert',
          autoHideDuration: 6000,
          alert: { color: 'success' },
        })
      );
    }
  }, [isOnline, dispatch, network]);

  // Set default entity ID from sessionStorage (run once on mount)
  useEffect(() => {
    const role = window.sessionStorage.getItem('roles');
    const entityId = window.sessionStorage.getItem('entityId');

    if (role === 'entityPrincipalAdmin' && entityId) {
      dispatch(changeDefaultEntityId(entityId));
    }
  }, [dispatch]); // Only dispatch is needed — it's stable

  return (
    <>
      {/* Logo & Toggle Button */}
      <Box
        sx={{
          width: 228,
          display: 'flex',
          [theme.breakpoints.down('lg')]: { width: 'auto' },
        }}
      >
        <Box
          component='span'
          sx={{ display: { xs: 'none', lg: 'block' }, flexGrow: 1 }}
        >
          <Stack direction='row' alignItems='center'>
            <LogoSection dark />
            <Typography variant='h3' sx={{ marginLeft: 2 }}>
              SPG
            </Typography>
          </Stack>
        </Box>

        <Avatar
          variant='rounded'
          sx={{
            ...theme.typography.commonAvatar,
            ...theme.typography.mediumAvatar,
            overflow: 'hidden',
            transition: 'all .2s ease-in-out',
            background:
              theme.palette.mode === 'dark'
                ? theme.palette.dark.main
                : theme.palette.secondary.light,
            color:
              theme.palette.mode === 'dark'
                ? theme.palette.secondary.main
                : theme.palette.grey[900],
            '&:hover': {
              background:
                theme.palette.mode === 'dark'
                  ? theme.palette.secondary.main
                  : theme.palette.paper,
              color:
                theme.palette.mode === 'dark'
                  ? theme.palette.secondary.light
                  : theme.palette.grey[900],
              boxShadow: 'none',
            },
            display: { lg: 'none', xs: 'flex' },
            justifyContent: 'start',
            height: '48px',
            width: '48px',
          }}
          onClick={() => dispatch(openDrawer(!drawerOpen))}
          color='inherit'
        >
          <IconMenu2 stroke={1.5} size='1.3rem' />
        </Avatar>
      </Box>

      {/* Entity Select */}
      {(user?.entities || window.sessionStorage.getItem('entityId')) && (
        <RoleGuard permissionOn='entity' permissionOnType='fetch'>
          <EntitySelect />
        </RoleGuard>
      )}

      {/* Notification & Profile */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'end' }}
      >
        <NotificationSection />
        <ProfileSection />
      </Box>
    </>
  );
};

export default Header;
